import React, { useState, useRef, useEffect } from 'react';
import ProductAutocomplete from './ProductAutocomplete';
import { calcItemUSD, totalDays, fmtBRL, fmtUSD } from '../utils/helpers';
import api, { quotations as qApi, alertSettings, exchangeRate, invoiceImport } from '../utils/api';
import GarmentGrid from './GarmentGrid';
import { SIZES, parseGrid, parseColors, colorTotal, garmentQty, isTechPack } from '../utils/sizeGrid';

const EMPTY_ITEM = { product:'',specification:'',ncm:'',details:'',moq:'',quantity:'',initial_price:'',target_price:'',sampling_days:'',production_days:'',currency:'USD',is_development:false,product_id:null,cbm:'',weight_kg:'',is_garment:false,size_grid:{},color_grid:[] };

// Chave estável por linha (não vai ao backend): os arquivos pendentes de itens
// ainda não salvos ficam presos a ela — por índice eles trocavam de item ao
// remover/copiar linhas.
let KEY_SEQ = 0;
const withKey = it => ({ ...it, size_grid: parseGrid(it.size_grid), color_grid: parseColors(it.color_grid), _key: `k${++KEY_SEQ}` });
const TECH_PACK_ACCEPT = '.pdf,.xlsx,.xls,.doc,.docx,.ai,.png,.jpg,.jpeg,.webp';
// Limite por arquivo (igual ao backend). Fichas técnicas em PDF com fotos são grandes.
const MAX_UPLOAD_MB = 100;
const fmtMB = b => `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;

// Separa os arquivos acima do limite e avisa antes de enviar.
function rejectOversized(files) {
  const big = files.filter(f => f.size > MAX_UPLOAD_MB * 1024 * 1024);
  if (big.length) {
    alert(`Arquivo(s) acima de ${MAX_UPLOAD_MB} MB não foram anexados:\n` +
      big.map(f => `• ${f.name} (${fmtMB(f.size)})`).join('\n') +
      '\n\nDica: exporte o PDF em qualidade menor ou divida em partes.');
  }
  return files.filter(f => !big.includes(f));
}

// Envia UM arquivo por requisição: vários juntos estouravam o limite do
// servidor (413) mesmo com cada arquivo pequeno, e um erro derrubava todos.
async function uploadItemFiles(itemId, files, category) {
  const ok = [], failed = [];
  for (const f of files) {
    const fd = new FormData();
    fd.append('category', category);
    fd.append('files', f);
    try {
      const { data } = await api.post(`/items/${itemId}/files`, fd);
      ok.push(...(data || []));
    } catch (err) {
      const msg = err.response?.status === 413
        ? `maior que o limite do servidor (${fmtMB(f.size)})`
        : (err.response?.data?.error || err.message);
      failed.push(`• ${f.name}: ${msg}`);
    }
  }
  if (failed.length) alert('Falha ao enviar arquivo(s):\n' + failed.join('\n'));
  return ok;
}

export default function QuotationForm({ quotation, suppliers, agents = [], onSave, onClose, onAfterCreate, onFilesUploaded }) {
  const isEdit = !!quotation?.id;
  const [form, setForm] = useState({
    supplier_id: quotation?.supplier_id||'',
    agent_id: quotation?.agent_id||'',
    collection:  quotation?.collection||'',
    exchange_rate: quotation?.exchange_rate||5.20,
    other_costs_multiplier: quotation?.other_costs_multiplier||1.6,
    rmb_rate: quotation?.rmb_rate||6.7,
    currency: quotation?.currency||'USD',
    transit_days: quotation?.id
      ? ((quotation.shipping_days||0) + (quotation.customs_days||0))
      : 75,
    notes: quotation?.notes||'',
    cost_center: quotation?.cost_center||'',
    incoterm: quotation?.incoterm||'FOB',
    estimated_freight_usd: quotation?.estimated_freight_usd||0,
  });
  const [defaultProdDays, setDefaultProdDays] = useState(30);
  const [rmbAutoFetched, setRmbAutoFetched] = useState(false);

  // Load current exchange rate (always) and multiplier default (new quotations only)
  useEffect(() => {
    Promise.all([
      isEdit ? Promise.resolve(null) : alertSettings.get().catch(() => null),
      exchangeRate.get().catch(() => null),
    ]).then(([s, fx]) => {
      setForm(f => ({
        ...f,
        ...(s?.other_costs_multiplier ? { other_costs_multiplier: s.other_costs_multiplier } : {}),
        ...(fx?.rate_with_buffer ? { exchange_rate: fx.rate_with_buffer } : {}),
        ...(fx?.rmb_per_usd ? { rmb_rate: fx.rmb_per_usd } : {}),
      }));
      if (fx?.rmb_per_usd) setRmbAutoFetched(true);
    });
  }, []); // eslint-disable-line
  const [items, setItems]   = useState(() => (quotation?.items?.length ? quotation.items : [{ ...EMPTY_ITEM, production_days: 30 }]).map(withKey));
  const [photos, setPhotos] = useState(quotation?.photos||[]);
  const [saving, setSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState({});        // _key → File[]
  const [pendingTechPacks, setPendingTechPacks] = useState({}); // _key → File[] (ficha técnica)
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const fileRef = useRef();
  const numThRef = useRef();
  const [numColW, setNumColW] = useState(34);
  useEffect(() => {
    const measure = () => { const w = numThRef.current?.offsetWidth; if (w) setNumColW(w); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const templateRef = useRef();
  const invoiceRef = useRef();

  // Invoice import state
  const [invoiceParsing, setInvoiceParsing] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null); // { items, meta, warning }

  const selectedSupplier = suppliers.find(s => s.id === form.supplier_id);
  function setField(k,v) { setForm(f => ({...f, [k]:v})); }
  function setItem(idx,k,v) { setItems(p => p.map((it,i)=>i===idx?{...it,[k]:v}:it)); }
  function addItem() { setItems(p=>[...p,withKey({...EMPTY_ITEM, production_days: defaultProdDays||''})]); }
  // Roupa pronta: liga já com a grade XXS→L selecionada (qtd em branco)
  function toggleGarment(idx) {
    setItems(p => p.map((it,i) => {
      if (i !== idx) return it;
      if (it.is_garment) return { ...it, is_garment: false };
      const grid = Object.keys(parseGrid(it.size_grid)).length ? parseGrid(it.size_grid) : Object.fromEntries(SIZES.map(s => [s, '']));
      return { ...it, is_garment: true, size_grid: grid };
    }));
  }
  // g = { size_grid, color_grid } (ver GarmentGrid)
  function setGrid(idx, g) {
    setItems(p => p.map((it,i) => {
      if (i !== idx) return it;
      const next = { ...it, size_grid: g.size_grid, color_grid: g.color_grid || [] };
      const total = garmentQty(next);
      return { ...next, quantity: total > 0 ? total : it.quantity };
    }));
  }
  // Copia a grade deste item (tamanhos, cores e quantidades) para as demais roupas
  function applyGridToAll(idx) {
    const src = items[idx] || {};
    setItems(p => p.map((it,i) => {
      if (i === idx || !it.is_garment) return it;
      const next = { ...it, size_grid: { ...(src.size_grid || {}) },
        color_grid: (src.color_grid || []).map(c => ({ ...c, sizes: { ...c.sizes } })) };
      const total = garmentQty(next);
      return { ...next, quantity: total > 0 ? total : it.quantity };
    }));
  }
  // Remover o produto apaga também as fotos e a ficha técnica dele (no servidor,
  // ao salvar). Pede confirmação quando há arquivos.
  function removeItem(idx) {
    if (items.length === 1) return;
    const it = items[idx];
    const saved = it.files || [];
    const techN = saved.filter(isTechPack).length + (pendingTechPacks[it._key] || []).length;
    const otherN = saved.filter(f => !isTechPack(f)).length + (pendingFiles[it._key] || []).length;
    if (techN + otherN > 0) {
      const parts = [otherN ? `${otherN} foto(s)/arquivo(s)` : '', techN ? `${techN} ficha(s) técnica(s)` : ''].filter(Boolean).join(' e ');
      if (!window.confirm(`Remover "${it.product || 'item'}"?\n\n${parts} deste produto também ${techN + otherN > 1 ? 'serão apagados' : 'será apagado'}${saved.length ? ' ao salvar' : ''}.`)) return;
    }
    setItems(p => p.filter((_, i) => i !== idx));
    const drop = m => { const n = { ...m }; delete n[it._key]; return n; };
    setPendingFiles(drop);
    setPendingTechPacks(drop);
  }
  function copyItem(idx) {
    setItems(p => {
      const copy = withKey({ ...p[idx], id: undefined, files: [] });
      const next = [...p];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  }
  function applyProdDays() { setItems(p=>p.map(it=>({...it, production_days: defaultProdDays||''}))); }

  async function handlePhotoUpload(e) {
    const files = Array.from(e.target.files); if (!files.length) return;
    if (!quotation?.id) {
      setPendingPhotos(prev => [...prev, ...files.map(f => ({
        file: f, previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null
      }))]);
      e.target.value = ''; return;
    }
    const fd = new FormData(); files.forEach(f=>fd.append('photos',f));
    const { data } = await api.post(`/quotations/${quotation.id}/photos`, fd);
    setPhotos(p=>[...p,...data]);
    e.target.value = '';
  }

  async function handleDeletePhoto(id) {
    await api.delete(`/photos/${id}`);
    setPhotos(p=>p.filter(x=>x.id!==id));
  }

  async function handleImportTemplate(e) {
    const file = e.target.files[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const { items: imported } = await qApi.parseTemplate(fd);
      if (imported?.length) {
        setItems(imported.map(it => withKey({ ...EMPTY_ITEM, ...it })));
      } else {
        alert('Nenhum produto encontrado. Verifique os cabeçalhos do arquivo.');
      }
    } catch(err) {
      alert('Erro ao importar: ' + (err.response?.data?.error || err.message));
    } finally { e.target.value = ''; }
  }

  async function handleInvoiceFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setInvoiceParsing(true);
    try {
      const result = await invoiceImport.parse(file);
      // Make items editable by cloning them
      setInvoicePreview({
        items: (result.items || []).map(it => ({ ...it })),
        meta: result.meta || {},
        warning: result.warning || '',
      });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Erro ao processar arquivo.';
      alert('Erro ao importar PI/Invoice: ' + msg);
    } finally {
      setInvoiceParsing(false);
    }
  }

  function handleInvoiceConfirm() {
    if (!invoicePreview) return;
    const { items: previewItems, meta } = invoicePreview;
    const newItems = previewItems.map(it => withKey({
      ...EMPTY_ITEM,
      product: it.product || '',
      specification: it.specification || '',
      ncm: it.ncm || '',
      quantity: it.quantity || '',
      initial_price: it.unit_price || '',
      target_price: it.unit_price || '',
      production_days: defaultProdDays || '',
    }));
    setItems(prev => [...prev, ...newItems]);
    // Optionally apply meta fields if present and form fields are empty/default
    if (meta.freight && (!form.estimated_freight_usd || form.estimated_freight_usd === 0)) {
      setField('estimated_freight_usd', parseFloat(meta.freight) || 0);
    }
    setInvoicePreview(null);
  }

  // category: 'general' (fotos/documentos) ou 'tech_pack' (ficha técnica)
  async function handleItemFiles(itemIdOrKey, e, isNewItem, category = 'general') {
    const files = rejectOversized(Array.from(e.target.files));
    if (!files.length) { e.target.value = ''; return; }
    const itemIdOrIdx = itemIdOrKey;
    if (isNewItem) {
      const setPending = category === 'tech_pack' ? setPendingTechPacks : setPendingFiles;
      setPending(prev => ({ ...prev, [itemIdOrIdx]: [...(prev[itemIdOrIdx]||[]), ...files] }));
      e.target.value = ''; return;
    }
    e.target.value = '';
    const uploaded = await uploadItemFiles(itemIdOrIdx, files, category);
    if (uploaded.length) {
      setItems(prev => prev.map(it =>
        it.id === itemIdOrIdx ? { ...it, files: [...(it.files||[]), ...uploaded] } : it
      ));
    }
  }

  async function handleDeleteItemFile(fileId, itemId) {
    try {
      await api.delete(`/item-files/${fileId}`);
      setItems(prev => prev.map(it =>
        it.id === itemId ? { ...it, files: (it.files||[]).filter(f => f.id !== fileId) } : it
      ));
    } catch(err) {
      alert('Erro ao deletar arquivo: ' + (err.response?.data?.error || err.message));
    }
  }

  function removePendingFile(key, fileIdx, category = 'general') {
    const setPending = category === 'tech_pack' ? setPendingTechPacks : setPendingFiles;
    setPending(prev => {
      const arr = [...(prev[key]||[])];
      arr.splice(fileIdx, 1);
      return { ...prev, [key]: arr };
    });
  }

  async function handleSave() {
    if (!form.supplier_id) return alert('Select a supplier.');
    if (items.some(i=>!i.product)) return alert('Fill product name in all items.');
    const noGrid = items.find(i => i.is_garment && !Object.keys(parseGrid(i.size_grid)).length);
    if (noGrid) return alert(`Selecione ao menos um tamanho na grade de "${noGrid.product}".`);
    const noColorName = items.find(i => i.is_garment && parseColors(i.color_grid).some(c => !String(c.color).trim() && colorTotal(c) > 0));
    if (noColorName) return alert(`Informe o nome da cor em todas as linhas com quantidade de "${noColorName.product}".`);
    setSaving(true);
    try {
      const { transit_days, ...formRest } = form;
      const payload = { ...formRest, shipping_days: transit_days, customs_days: 0,
        items: items.map(({ _key, ...it }) => it) };
      const result = await onSave(payload);
      // Arquivos de itens ainda não salvos (cotação nova ou linha nova na edição):
      // o backend grava sort_order = posição na lista, então o índice casa.
      const qid = result?.id || quotation?.id;
      const hasPending = [pendingFiles, pendingTechPacks].some(m => Object.values(m).some(f => f?.length));
      if (qid && hasPending) {
        const saved = await qApi.get(qid);
        for (let i = 0; i < items.length; i++) {
          const target = saved.items?.[i];
          if (!target) continue;
          for (const [category, map] of [['general', pendingFiles], ['tech_pack', pendingTechPacks]]) {
            const files = map[items[i]._key] || [];
            if (!files.length) continue;
            await uploadItemFiles(target.id, files, category);
          }
        }
        if (!result?.id) onFilesUploaded?.();
      }
      if (result?.id) {
        if (pendingPhotos.length) {
          const fd = new FormData();
          pendingPhotos.forEach(p => fd.append('photos', p.file));
          await api.post(`/quotations/${result.id}/photos`, fd);
          pendingPhotos.forEach(p => { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl); });
        }
        onAfterCreate?.(result.id);
      }
    } finally { setSaving(false); }
  }

  const totalUSD = items.reduce((s,i) => s+calcItemUSD(i,form.rmb_rate,form.currency),0);
  const fobUSD = totalUSD;
  const totalBRL = totalUSD * (form.exchange_rate || 5.20) * (form.other_costs_multiplier || 1.6);

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <div className="modal-title">{isEdit?`✏️ Edit — ${quotation.reference}`:'📋 New Quotation Request'}</div>
            {selectedSupplier && <div style={{fontSize:12,color:'var(--gray-500)',marginTop:2}}>{selectedSupplier.name} · {selectedSupplier.contact_name} · {selectedSupplier.contact_email}</div>}
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            {!isEdit && (
              <>
                <input ref={templateRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={handleImportTemplate} />
                <button className="btn btn-ghost btn-sm" onClick={()=>templateRef.current?.click()}>📥 Importar Itens</button>
                <input ref={invoiceRef} type="file" accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg,.webp" style={{display:'none'}} onChange={handleInvoiceFile} />
                <button className="btn btn-ghost btn-sm" onClick={()=>invoiceRef.current?.click()} disabled={invoiceParsing}>
                  {invoiceParsing ? '⏳ Lendo...' : '📄 Importar PI/Invoice'}
                </button>
              </>
            )}
            <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-body">
          {/* General */}
          <div className="section-title">GENERAL</div>
          <div className="form-grid" style={{marginBottom:20}}>
            <div className="form-group">
              <label>Supplier *</label>
              <select className="highlight" value={form.supplier_id} onChange={e=>setField('supplier_id',e.target.value)}>
                <option value="">— Select Supplier —</option>
                {suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Agente Responsável</label>
              <select value={form.agent_id||''} onChange={e=>setField('agent_id',e.target.value||null)}>
                <option value="">— Sem agente —</option>
                {agents.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
                {form.agent_id && !agents.find(a=>a.id===form.agent_id) && quotation?.agent_name && (
                  <option key={form.agent_id} value={form.agent_id}>{quotation.agent_name} (inativo)</option>
                )}
              </select>
            </div>
            <div className="form-group">
              <label>Collection / Reference</label>
              <input value={form.collection} onChange={e=>setField('collection',e.target.value)} placeholder="SS27, AW27..." />
            </div>
            <div className="form-group">
              <label>Centro de Custo</label>
              <select value={form.cost_center||''} onChange={e=>setField('cost_center',e.target.value||null)}>
                <option value="">— Não definido —</option>
                <option value="producao">🏭 Produção</option>
                <option value="marketing">📣 Marketing</option>
              </select>
            </div>
          </div>

          {/* Parameters */}
          <div className="section-title">CALCULATION PARAMETERS</div>
          <div style={{background:'var(--accent-light)',border:'1.5px solid var(--accent)',borderRadius:'var(--radius)',padding:'14px 16px',marginBottom:20}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))',gap:16}}>
              <div className="form-group">
                <label>💱 Câmbio (USD → BRL)</label>
                <input type="number" step="0.01" value={form.exchange_rate} onChange={e=>setField('exchange_rate',parseFloat(e.target.value)||0)} className="highlight" />
                <span className="form-hint">Taxa atual + 5% de segurança</span>
              </div>
              <div className="form-group">
                <label>× Multiplicador de Custos</label>
                <input type="number" step="0.01" min="1" value={form.other_costs_multiplier} onChange={e=>setField('other_costs_multiplier',parseFloat(e.target.value)||1.6)} className="highlight" />
                <span className="form-hint">Frete + impostos + taxas (estimado). Ex: 1.6 = +60%</span>
              </div>
              <div className="form-group">
                <label>💱 Moeda dos Preços</label>
                <div style={{display:'flex',border:'1.5px solid var(--accent)',borderRadius:'var(--radius)',overflow:'hidden'}}>
                  <button type="button" onClick={()=>setField('currency','USD')}
                    style={{flex:1,padding:'9px 6px',fontWeight:700,fontSize:13,fontFamily:'var(--font-mono)',
                      cursor:'pointer',whiteSpace:'nowrap',transition:'background 0.15s',
                      border:'none',borderRight:'1px solid var(--accent)',
                      background:form.currency==='USD'?'var(--pantone)':'var(--white)',
                      color:form.currency==='USD'?'white':'var(--pantone)',
                    }}>$ USD</button>
                  <button type="button" onClick={()=>setField('currency','RMB')}
                    style={{flex:1,padding:'9px 6px',fontWeight:700,fontSize:13,fontFamily:'var(--font-mono)',
                      cursor:'pointer',whiteSpace:'nowrap',transition:'background 0.15s',
                      border:'none',
                      background:form.currency==='RMB'?'var(--pantone)':'var(--white)',
                      color:form.currency==='RMB'?'white':'var(--pantone)',
                    }}>¥ RMB</button>
                </div>
                <span className="form-hint">Moeda de todos os preços desta cotação</span>
              </div>
              <div className="form-group">
                <label>🇨🇳 Taxa RMB/USD {rmbAutoFetched && <span style={{fontSize:'0.75em',color:'var(--pantone)',fontWeight:400}}>(auto)</span>}</label>
                <input type="number" step="0.01" min="1" value={form.rmb_rate} onChange={e=>{ setRmbAutoFetched(false); setField('rmb_rate',parseFloat(e.target.value)||6.7); }} className={form.currency==='RMB'?'highlight':''} />
                <span className="form-hint">1 USD = {Number(form.rmb_rate||6.7).toFixed(2)} RMB · usado p/ calcular BRL</span>
              </div>
              <div className="form-group">
                <label>🚢 Transit Time (China → BR)</label>
                <input type="number" value={form.transit_days} onChange={e=>setField('transit_days',parseInt(e.target.value)||0)} className="highlight" />
                <span className="form-hint">Frete marítimo + desembaraço aduaneiro</span>
              </div>
              <div className="form-group">
                <label>📋 Incoterm</label>
                <select value={form.incoterm} onChange={e=>setField('incoterm',e.target.value)} className="highlight">
                  {['FOB','EXW','CIF','CFR','DDP','FCA'].map(t=><option key={t} value={t}>{t}</option>)}
                </select>
                <span className="form-hint">Padrão: FOB</span>
              </div>
              <div className="form-group">
                <label>✈ Frete Estimado (USD)</label>
                <input type="number" step="0.01" min="0" value={form.estimated_freight_usd||''} onChange={e=>setField('estimated_freight_usd',parseFloat(e.target.value)||0)} placeholder="0.00" />
                <span className="form-hint">Custo estimado de frete internacional</span>
              </div>
              <div className="form-group">
                <label>🏭 Prod. Time — padrão (dias)</label>
                <div style={{display:'flex',gap:6,alignItems:'center'}}>
                  <input type="number" value={defaultProdDays||''} onChange={e=>setDefaultProdDays(parseInt(e.target.value)||0)} className="highlight" style={{flex:1}} />
                  <button type="button" className="btn btn-ghost btn-sm" onClick={applyProdDays} title="Aplicar este valor em todos os itens" style={{whiteSpace:'nowrap',padding:'6px 10px'}}>↓ Todos</button>
                </div>
                <span className="form-hint">Novos itens usam este valor · clique "Todos" p/ aplicar em todos</span>
              </div>
            </div>
          </div>

          {/* Products */}
          <div className="flex-between" style={{marginBottom:12}}>
            <div className="section-title" style={{margin:0,border:'none',padding:0}}>PRODUCTS</div>
            <button className="btn btn-ghost btn-sm" onClick={addItem}>+ Add Item</button>
          </div>

          {/* Rolagem própria da tabela: cabeçalho fixo no topo, barra horizontal
              sempre visível e # + Produto congelados à esquerda */}
          <div className="items-scroll" style={{marginBottom:20}} data-testid="items-scroll">
            <table className="items-table">
              <thead><tr>
                <th ref={numThRef} className="sticky-col" style={{width:26,left:0}}>#</th>
                <th style={{width:46}} title="Produto a ser desenvolvido">Dev</th>
                <th style={{width:46}} title="Pedir amostra deste item">🧪</th>
                <th style={{width:46}} title="Roupa pronta — grade de tamanhos (XXS→L) e ficha técnica">👗</th>
                <th className="sticky-col sticky-col-last" style={{minWidth:150,left:numColW}}>Product *</th>
                <th style={{minWidth:140}}>Specification</th>
                <th style={{width:105}}>HS Code</th>
                <th style={{minWidth:130}}>Details</th>
                <th style={{width:70}}>MOQ</th>
                <th style={{width:72}}>Qty</th>
                <th style={{width:96}}>Init. Price</th>
                <th style={{width:96}}>Target Price</th>
                <th style={{width:78}}>Sampling</th>
                <th style={{width:78}}>Production</th>
                <th style={{width:64}} title="Volume por unidade (m³) — base do rateio de frete">CBM</th>
                <th style={{width:70}} title="Peso por unidade (kg) — base do rateio de frete">Peso kg</th>
                <th style={{width:86}}>Lead Time</th>
                <th style={{width:112}}>Est. USD</th>
                <th style={{width:112}}>Est. BRL</th>
                <th style={{width:70}}>Files</th>
                <th style={{width:32}}></th>
              </tr></thead>
              <tbody>
                {items.map((item,idx)=>{
                  const prazo = totalDays(item, form.transit_days, 0);
                  const costBRL = fobUSD > 0
                    ? (calcItemUSD(item, form.rmb_rate, form.currency) / fobUSD) * totalBRL
                    : 0;
                  const costUSD = calcItemUSD(item, form.rmb_rate, form.currency);
                  const isRMB = form.currency === 'RMB';
                  const iFiles = item.files||[];
                  // cor sólida: as colunas congeladas precisam cobrir o que rola por baixo
                  const rowBg = item.is_development ? '#FAF6F7' : 'var(--white, #fff)';
                  return (
                    <React.Fragment key={item._key}>
                    <tr style={{background:rowBg}}>
                      <td className="sticky-col" style={{color:'var(--gray-400)',textAlign:'center',fontSize:11,left:0,background:rowBg}}>{idx+1}</td>
                      <td style={{textAlign:'center'}}>
                        <button
                          type="button"
                          onClick={()=>setItem(idx,'is_development',!item.is_development)}
                          title={item.is_development ? 'Produto a desenvolver' : 'Marcar como desenvolvimento'}
                          style={{
                            background: item.is_development ? 'var(--pantone)' : 'var(--gray-100)',
                            color: item.is_development ? 'white' : 'var(--gray-400)',
                            border: `1.5px solid ${item.is_development ? 'var(--pantone)' : 'var(--gray-300)'}`,
                            borderRadius: 6, padding:'3px 7px', fontSize:11, fontWeight:700,
                            cursor:'pointer', whiteSpace:'nowrap', transition:'all 0.15s',
                          }}
                        >{item.is_development ? '🔧 DEV' : 'DEV'}</button>
                      </td>
                      <td style={{textAlign:'center'}}>
                        <button
                          type="button"
                          onClick={()=>setItem(idx,'sample_requested',!item.sample_requested)}
                          title={item.sample_requested ? 'Amostra solicitada' : 'Solicitar amostra deste item'}
                          style={{
                            background: item.sample_requested ? '#0e7490' : 'var(--gray-100)',
                            color: item.sample_requested ? 'white' : 'var(--gray-400)',
                            border: `1.5px solid ${item.sample_requested ? '#0e7490' : 'var(--gray-300)'}`,
                            borderRadius: 6, padding:'3px 7px', fontSize:11, fontWeight:700,
                            cursor:'pointer', whiteSpace:'nowrap', transition:'all 0.15s',
                          }}
                        >{item.sample_requested ? '🧪' : '🧪'}</button>
                      </td>
                      <td style={{textAlign:'center'}}>
                        <button
                          type="button"
                          onClick={()=>toggleGarment(idx)}
                          aria-pressed={!!item.is_garment}
                          title={item.is_garment ? 'Roupa pronta — clique para desmarcar' : 'Marcar como roupa pronta (grade + ficha técnica)'}
                          style={{
                            background: item.is_garment ? 'var(--pantone)' : 'var(--gray-100)',
                            color: item.is_garment ? 'white' : 'var(--gray-400)',
                            border: `1.5px solid ${item.is_garment ? 'var(--pantone)' : 'var(--gray-300)'}`,
                            borderRadius: 6, padding:'3px 7px', fontSize:11, fontWeight:700,
                            cursor:'pointer', whiteSpace:'nowrap', transition:'all 0.15s',
                          }}
                        >👗</button>
                      </td>
                      <td className="sticky-col sticky-col-last" style={{left:numColW,background:rowBg}}><ProductAutocomplete value={item.product} productId={item.product_id} onChange={({product,product_id})=>{setItem(idx,'product',product);setItem(idx,'product_id',product_id);}} /></td>
                      <td><input value={item.specification} onChange={e=>setItem(idx,'specification',e.target.value)} placeholder="Spec..." /></td>
                      <td><input value={item.ncm} onChange={e=>setItem(idx,'ncm',e.target.value)} placeholder="0000.00.00" title="Pode digitar só os números (ex.: 33030010) ou com pontos (3303.00.10) — o sistema entende os dois." /></td>
                      <td><input value={item.details} onChange={e=>setItem(idx,'details',e.target.value)} placeholder="Notes..." /></td>
                      <td><input type="number" value={item.moq||''} onChange={e=>setItem(idx,'moq',e.target.value)} /></td>
                      <td>
                        {item.is_garment && garmentQty(item) > 0
                          ? <input type="number" value={garmentQty(item)} readOnly title="Soma da grade de tamanhos" className="highlight" style={{background:'var(--gray-100)',fontWeight:700}} />
                          : <input type="number" value={item.quantity||''} onChange={e=>setItem(idx,'quantity',e.target.value)} className="highlight" />}
                      </td>
                      <td><input type="number" step="0.01" value={item.initial_price||''} onChange={e=>setItem(idx,'initial_price',e.target.value)} placeholder={isRMB?'¥':'$'} /></td>
                      <td><input type="number" step="0.01" value={item.target_price||''} onChange={e=>setItem(idx,'target_price',e.target.value)} className="highlight" placeholder={isRMB?'¥':'$'} /></td>
                      <td><input type="number" value={item.sampling_days||''} onChange={e=>setItem(idx,'sampling_days',e.target.value)} /></td>
                      <td><input type="number" value={item.production_days||''} onChange={e=>setItem(idx,'production_days',e.target.value)} /></td>
                      <td><input type="number" step="0.001" value={item.cbm||''} onChange={e=>setItem(idx,'cbm',e.target.value)} placeholder="m³" /></td>
                      <td><input type="number" step="0.01" value={item.weight_kg||''} onChange={e=>setItem(idx,'weight_kg',e.target.value)} placeholder="kg" /></td>
                      <td style={{textAlign:'center',fontWeight:600,fontSize:12,color:'var(--navy)'}}>{prazo>0?`${prazo}d`:'—'}</td>
                      <td style={{textAlign:'right',fontSize:11,color:'var(--blue)',fontFamily:'var(--font-mono)'}}>{costUSD>0?fmtUSD(costUSD):'—'}</td>
                      <td style={{textAlign:'right',fontWeight:700,fontSize:11,color:'var(--green)',fontFamily:'var(--font-mono)'}}>{costBRL>0?fmtBRL(costBRL):'—'}</td>
                      <td style={{minWidth:80}}>
                        {isEdit && item.id ? (
                          <div style={{display:'flex',flexWrap:'wrap',gap:3,alignItems:'center',justifyContent:'center'}}>
                            {iFiles.filter(f => !(item.is_garment && isTechPack(f))).map(f => (
                              <div key={f.id} style={{position:'relative',display:'inline-block'}} title={isTechPack(f) ? `Ficha técnica: ${f.original_name}` : f.original_name}>
                                {isTechPack(f)
                                  ? <span style={{fontSize:20,lineHeight:'30px',display:'block',textAlign:'center'}}>📐</span>
                                  : f.file_type==='image'
                                  ? <img src={`/uploads/${f.filename}`} alt={f.original_name}
                                      style={{width:30,height:30,objectFit:'cover',borderRadius:3,border:'1px solid var(--gray-200)',display:'block'}} />
                                  : <span style={{fontSize:20,lineHeight:'30px',display:'block',textAlign:'center'}}>📄</span>
                                }
                                <button onClick={()=>handleDeleteItemFile(f.id,item.id)}
                                  style={{position:'absolute',top:-4,right:-4,background:'var(--red,#dc3545)',color:'white',border:'none',borderRadius:'50%',width:14,height:14,fontSize:9,cursor:'pointer',lineHeight:'14px',padding:0,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
                              </div>
                            ))}
                            <label style={{cursor:'pointer',fontSize:16,color:'var(--pantone)',lineHeight:'30px',padding:'0 2px'}} title="Adicionar arquivo">
                              ➕
                              <input type="file" multiple style={{display:'none'}} onChange={e=>handleItemFiles(item.id,e,false)} />
                            </label>
                          </div>
                        ) : (
                          <div style={{display:'flex',flexWrap:'wrap',gap:3,alignItems:'center',justifyContent:'center'}}>
                            {(pendingFiles[item._key]||[]).map((f,fi) => (
                              <div key={fi} style={{position:'relative',display:'inline-block'}}>
                                {f.type?.startsWith('image/')
                                  ? <img src={URL.createObjectURL(f)} alt={f.name}
                                      style={{width:30,height:30,objectFit:'cover',borderRadius:3,border:'1px solid var(--gray-200)',display:'block'}} />
                                  : <span style={{fontSize:20,lineHeight:'30px',display:'block',textAlign:'center'}}>📄</span>
                                }
                                <button onClick={()=>removePendingFile(item._key,fi)}
                                  style={{position:'absolute',top:-4,right:-4,background:'var(--red,#dc3545)',color:'white',border:'none',borderRadius:'50%',width:14,height:14,fontSize:9,cursor:'pointer',lineHeight:'14px',padding:0,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
                              </div>
                            ))}
                            <label style={{cursor:'pointer',fontSize:16,color:'var(--pantone)',lineHeight:'30px',padding:'0 2px'}} title="Adicionar arquivo">
                              ➕
                              <input type="file" multiple style={{display:'none'}} onChange={e=>handleItemFiles(item._key,e,true)} />
                            </label>
                          </div>
                        )}
                      </td>
                      <td>
                        <button type="button" className="btn btn-ghost btn-sm" title="Copiar linha" onClick={() => copyItem(idx)} style={{ fontSize: 13 }}>⧉</button>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={()=>removeItem(idx)} disabled={items.length===1}>✕</button>
                      </td>
                    </tr>
                    {!!item.is_garment && (
                      <GarmentRow
                        item={item} idx={idx} colSpan={21}
                        canApplyAll={items.filter(it => it.is_garment).length > 1}
                        onGrid={g => setGrid(idx, g)}
                        onApplyAll={() => applyGridToAll(idx)}
                        techPacks={(item.files||[]).filter(isTechPack)}
                        pendingTechPacks={pendingTechPacks[item._key]||[]}
                        onUpload={e => (isEdit && item.id) ? handleItemFiles(item.id, e, false, 'tech_pack') : handleItemFiles(item._key, e, true, 'tech_pack')}
                        onDelete={f => handleDeleteItemFile(f.id, item.id)}
                        onRemovePending={fi => removePendingFile(item._key, fi, 'tech_pack')}
                      />
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(totalUSD>0||totalBRL>0) && (
            <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginBottom:20}}>
              {totalUSD>0 && <div style={{background:'var(--gray-100)',borderRadius:'var(--radius)',padding:'8px 16px',fontFamily:'var(--font-mono)',fontSize:13,fontWeight:700,color:'var(--navy)'}}>Total USD: {fmtUSD(totalUSD)}</div>}
              {totalBRL>0 && <div style={{background:'var(--pantone)',color:'white',borderRadius:'var(--radius)',padding:'8px 16px',fontFamily:'var(--font-mono)',fontSize:13,fontWeight:700}}>Total BRL: {fmtBRL(totalBRL)}</div>}
            </div>
          )}

          {/* Notes */}
          <div className="section-title">NOTES</div>
          <div className="form-group" style={{marginBottom:20}}>
            <textarea value={form.notes} onChange={e=>setField('notes',e.target.value)} placeholder="General notes for trading or supplier..." />
          </div>

          {/* Photos / Attachments */}
          <>
            <div className="section-title">PHOTOS / ATTACHMENTS</div>
            <div className="upload-zone" onClick={()=>fileRef.current?.click()}>📎 Clique para anexar arquivos (imagens, PDF, Excel…)</div>
            <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" style={{display:'none'}} onChange={handlePhotoUpload} />
            {(photos.length > 0 || pendingPhotos.length > 0) && (
              <div className="photo-grid" style={{marginTop:10}}>
                {photos.map(p=>{
                  const isImg = /\.(jpg|jpeg|png|gif|webp|heic)$/i.test(p.filename||'');
                  return (
                    <div key={p.id} style={{position:'relative'}}>
                      {isImg
                        ? <img src={`/uploads/${p.filename}`} alt="" className="photo-thumb" />
                        : <div className="photo-thumb" style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'var(--gray-100)',gap:2,fontSize:24}}>📄</div>
                      }
                      <button onClick={()=>handleDeletePhoto(p.id)} style={{position:'absolute',top:-5,right:-5,background:'var(--red)',color:'white',border:'none',borderRadius:'50%',width:17,height:17,fontSize:10,cursor:'pointer'}}>✕</button>
                    </div>
                  );
                })}
                {pendingPhotos.map((p,i)=>(
                  <div key={`pending-${i}`} style={{position:'relative'}}>
                    {p.previewUrl
                      ? <img src={p.previewUrl} alt="" className="photo-thumb" />
                      : <div className="photo-thumb" style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'var(--gray-100)',gap:2,padding:4}}>
                          <span style={{fontSize:24}}>📄</span>
                          <span style={{fontSize:9,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:70}}>{p.file.name}</span>
                        </div>
                    }
                    <button onClick={()=>setPendingPhotos(prev=>prev.filter((_,j)=>j!==i))} style={{position:'absolute',top:-5,right:-5,background:'var(--red)',color:'white',border:'none',borderRadius:'50%',width:17,height:17,fontSize:10,cursor:'pointer'}}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving?'⏳ Saving...':isEdit?'💾 Save Changes':'✅ Create Quotation'}
          </button>
        </div>
      </div>

      {/* Invoice Import Preview Modal */}
      {invoicePreview && (
        <div className="modal-overlay" style={{zIndex:1100}} onClick={e => { if (e.target === e.currentTarget) setInvoicePreview(null); }}>
          <div className="modal modal-lg" style={{maxHeight:'90vh',display:'flex',flexDirection:'column'}}>
            <div className="modal-header">
              <div className="modal-title">📄 Preview — PI/Invoice Importada</div>
              <button className="btn btn-ghost btn-sm btn-icon" onClick={()=>setInvoicePreview(null)}>✕</button>
            </div>
            <div className="modal-body" style={{flex:1,overflowY:'auto'}}>
              {invoicePreview.warning && (
                <div style={{background:'#fff3cd',border:'1.5px solid #ffc107',borderRadius:'var(--radius)',padding:'10px 14px',marginBottom:16,fontSize:13,color:'#856404'}}>
                  ⚠️ <strong>Atenção:</strong> {invoicePreview.warning}
                </div>
              )}

              {/* Meta info */}
              {(invoicePreview.meta.currency || invoicePreview.meta.supplier || invoicePreview.meta.freight) && (
                <div style={{background:'var(--gray-50,#f8f9fa)',border:'1px solid var(--gray-200)',borderRadius:'var(--radius)',padding:'10px 14px',marginBottom:16,fontSize:12,display:'flex',gap:20,flexWrap:'wrap'}}>
                  {invoicePreview.meta.supplier && <span><strong>Fornecedor:</strong> {invoicePreview.meta.supplier}</span>}
                  {invoicePreview.meta.currency && <span><strong>Moeda:</strong> {invoicePreview.meta.currency}</span>}
                  {invoicePreview.meta.freight && <span><strong>Frete:</strong> {invoicePreview.meta.freight}</span>}
                </div>
              )}

              <div className="section-title" style={{marginBottom:8}}>ITENS EXTRAÍDOS <span style={{fontWeight:400,fontSize:11,color:'var(--gray-500)'}}>— edite antes de confirmar</span></div>
              <div style={{overflowX:'auto'}}>
                <table className="items-table">
                  <thead><tr>
                    <th style={{width:26}}>#</th>
                    <th style={{minWidth:150}}>Produto</th>
                    <th style={{minWidth:140}}>Especificação</th>
                    <th style={{width:110}}>HS Code (NCM)</th>
                    <th style={{width:80}}>Qtd</th>
                    <th style={{width:110}}>Preço Unit.</th>
                    <th style={{width:32}}></th>
                  </tr></thead>
                  <tbody>
                    {invoicePreview.items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{color:'var(--gray-400)',textAlign:'center',fontSize:11}}>{idx+1}</td>
                        <td><input value={item.product||''} onChange={e => setInvoicePreview(p => ({ ...p, items: p.items.map((it,i) => i===idx ? {...it,product:e.target.value} : it) }))} placeholder="Produto..." /></td>
                        <td><input value={item.specification||''} onChange={e => setInvoicePreview(p => ({ ...p, items: p.items.map((it,i) => i===idx ? {...it,specification:e.target.value} : it) }))} placeholder="Especificação..." /></td>
                        <td><input value={item.ncm||''} onChange={e => setInvoicePreview(p => ({ ...p, items: p.items.map((it,i) => i===idx ? {...it,ncm:e.target.value} : it) }))} placeholder="0000.00.00" title="Pode digitar só os números (ex.: 33030010) ou com pontos (3303.00.10) — o sistema entende os dois." /></td>
                        <td><input type="number" value={item.quantity||''} onChange={e => setInvoicePreview(p => ({ ...p, items: p.items.map((it,i) => i===idx ? {...it,quantity:e.target.value} : it) }))} /></td>
                        <td><input type="number" step="0.01" value={item.unit_price||''} onChange={e => setInvoicePreview(p => ({ ...p, items: p.items.map((it,i) => i===idx ? {...it,unit_price:e.target.value} : it) }))} placeholder="0.00" /></td>
                        <td>
                          <button className="btn btn-danger btn-sm btn-icon" onClick={() => setInvoicePreview(p => ({ ...p, items: p.items.filter((_,i) => i!==idx) }))} disabled={invoicePreview.items.length === 1}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {invoicePreview.items.length === 0 && (
                <div style={{textAlign:'center',padding:'20px',color:'var(--gray-400)',fontSize:13}}>Nenhum item extraído.</div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setInvoicePreview(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleInvoiceConfirm} disabled={invoicePreview.items.length === 0}>
                ✅ Adicionar à cotação ({invoicePreview.items.length} {invoicePreview.items.length === 1 ? 'item' : 'itens'})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-linha do item roupa pronta: grade do pedido + ficha técnica ─────────
function GarmentRow({ item, colSpan, canApplyAll, onGrid, onApplyAll, techPacks, pendingTechPacks, onUpload, onDelete, onRemovePending }) {
  const fileRef = useRef();
  const chip = { display:'inline-flex', alignItems:'center', gap:6, background:'var(--white, #fff)', border:'1px solid var(--accent)',
    borderRadius:6, padding:'3px 8px', fontSize:12, maxWidth:220 };
  const name = { overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' };
  const x = { background:'none', border:'none', color:'var(--red,#dc3545)', cursor:'pointer', fontSize:12, padding:0 };
  return (
    <tr data-testid="garment-row">
      <td colSpan={colSpan} style={{ background:'var(--accent-light, #f7f0f2)', borderTop:'none', padding:'8px 12px 10px 40px' }}>
        <div style={{ display:'flex', gap:28, flexWrap:'wrap', alignItems:'flex-start', position:'sticky', left:40, width:'fit-content', maxWidth:'calc(100vw - 160px)' }}>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:'var(--pantone)', textTransform:'uppercase', letterSpacing:.4, marginBottom:5 }}>
              👗 Grade do pedido{parseColors(item.color_grid).length ? ' por cor' : ''} — {item.product || 'item'}
            </div>
            <div style={{ display:'flex', alignItems:'flex-end', gap:8 }}>
              <GarmentGrid value={{ size_grid: item.size_grid, color_grid: item.color_grid }} onChange={onGrid} />
              {canApplyAll && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={onApplyAll}
                  title="Copiar esta grade para todos os itens de roupa pronta" style={{ fontSize:11, whiteSpace:'nowrap' }}>
                  ↓ Grade p/ todas
                </button>
              )}
            </div>
          </div>
          <div style={{ minWidth:220 }}>
            <div style={{ fontSize:10, fontWeight:700, color:'var(--pantone)', textTransform:'uppercase', letterSpacing:.4, marginBottom:5 }}>
              📐 Ficha técnica
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, alignItems:'center' }}>
              {techPacks.map(f => (
                <span key={f.id} style={chip}>
                  <a href={`/uploads/${f.filename}`} target="_blank" rel="noreferrer" style={name} title={f.original_name}>📐 {f.original_name}</a>
                  <button type="button" style={x} onClick={() => onDelete(f)} title="Remover ficha técnica">✕</button>
                </span>
              ))}
              {pendingTechPacks.map((f, fi) => (
                <span key={`p${fi}`} style={{ ...chip, borderStyle:'dashed' }} title="Será enviada ao salvar">
                  <span style={name}>📐 {f.name}</span>
                  <button type="button" style={x} onClick={() => onRemovePending(fi)} title="Remover">✕</button>
                </span>
              ))}
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()} style={{ fontSize:11 }}>
                ＋ Anexar ficha técnica
              </button>
              <input ref={fileRef} type="file" multiple accept={TECH_PACK_ACCEPT} style={{ display:'none' }}
                data-testid="tech-pack-input" onChange={onUpload} />
            </div>
            {!techPacks.length && !pendingTechPacks.length && (
              <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:4 }}>PDF, Excel, imagem ou .ai — vai para o fornecedor no Excel e no .zip</div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}
